import React, { useMemo, useState, useCallback } from 'react'
import { provider } from 'web3-core'
import BigNumber from 'bignumber.js'
import { BLOCKS_PER_YEAR } from 'config'
import useI18n from 'hooks/useI18n'
import styled, { keyframes } from 'styled-components'
import { Flex, Text, LinkExternal, Link } from '@pancakeswap-libs/uikit'
import ExpandableSectionButton from 'components/ExpandableSectionButton'
import { getEbenAddress, isAddressMatch } from 'utils/addressHelpers'
import { sortLpTokens } from 'utils/lpUtils'
import { isFavorite, addFavorite, removeFavorite } from 'utils/ranchUtils'
import { usePriceBchUsd } from 'state/hooks'
import useBlock from 'hooks/useBlock'

import CardHeading from './CardHeading'
import CardActionsContainer from './CardActionsContainer'
import CardActions2Container from './CardActions2Container'
import DetailsSection from './DetailsSection'
import ApyButton from '../../Farms/components/FarmCard/ApyButton'


const RainbowLight = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`

const StyledCardAccent = styled.div`
  background: linear-gradient(45deg,
  rgba(255, 0, 0, 1) 0%,
  rgba(255, 154, 0, 1) 10%,
  rgba(208, 222, 33, 1) 20%,
  rgba(79, 220, 74, 1) 30%,
  rgba(63, 218, 216, 1) 40%,
  rgba(47, 201, 226, 1) 50%,
  rgba(28, 127, 238, 1) 60%,
  rgba(95, 21, 242, 1) 70%,
  rgba(186, 12, 248, 1) 80%,
  rgba(251, 7, 217, 1) 90%,
  rgba(255, 0, 0, 1) 100%);
  background-size: 300% 300%;
  animation: ${RainbowLight} 2s linear infinite;
  border-radius: 16px;
  filter: blur(6px);
  position: absolute;
  top: -2px;
  right: -2px;
  bottom: -2px;
  left: -2px;
  z-index: -1;
`

const FCard = styled.div`
  align-self: baseline;
  background: ${(props) => props.theme.card.background};
  border-radius: 32px;
  box-shadow: 0px 2px 12px -8px rgba(25, 19, 38, 0.1), 0px 1px 1px rgba(25, 19, 38, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 24px;
  position: relative;
  text-align: center;
`

const Divider = styled.div`
  background-color: ${({ theme }) => theme.colors.borderColor};
  height: 1px;
  margin: 28px auto;
  width: 100%;
`

const ExpandingWrapper = styled.div<{ expanded: boolean }>`
  height: ${(props) => (props.expanded ? '100%' : '0px')};
  overflow: hidden;
`

const ManagementLabel = styled(Text)`
  width: 100%; 
  text-align: center; 
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderColor}; 
  line-height: 0.1em;
  margin: 25px 0 10px 0; 

  & > span { 
    background: ${(props) => props.theme.card.background};
    padding:0 10px; 
  }
`

const FavoriteContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: 22px 30px;
`

const StyledFavoriteButton = styled.button`
  background-color: transparent;
  border: none;
  padding: 0;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 0;
  cursor: pointer;
  opacity: 1;

  &:hover {
    opacity: 0.9;
  }

  & > img {
    width: 32px;
    height: 32px;
  }
`

const StyledText2 = styled(Text)`
  display: inline-block;
  margin-right: 5px;
  font-weight: 600;
`

const StyledLinkExternal2 = styled(LinkExternal)`
  text-decoration: none;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.text};
  display: inline-block;
  position: relative;
  font-weight: 600;

  svg {
    position: relative;
    top: 3px;
    display: inline-block;
    vertical-align: top;
    padding-left: 4px;
    height: 18px;
    width: auto;
    fill: ${({ theme }) => theme.colors.primary};
  }
`

const Row2 = styled.div`
  text-align: right;
`

export interface CardProps {
  ranch: any,
  ethereum?: provider
  account?: string
  onFavoriteChanged?: (address: string, isFavorite: boolean) => void
}

const getDisplayBalance = (balance) => {
  const displayBalanceOption = balance > 1 ? {} : {maximumFractionDigits: 8};
  const displayBalance = navigator ? balance.toLocaleString(navigator.language, displayBalanceOption) : balance.toLocaleString();
  return displayBalance;
}

const Card: React.FC<CardProps> = ({ ranch, ethereum, account, onFavoriteChanged}) => {
  const TranslateString = useI18n();
  const bchPrice = usePriceBchUsd();
  const currentBlock = useBlock();

  // const totalValue: BigNumber = useMemo(() => {
  //   if (!ranch.lpTotalInQuoteToken) {
  //     return null
  //   }
  //   return new BigNumber(ranch.lpTotalInQuoteToken)
  // }, [ranch])
  // const lpPrice = totalValue ? totalValue.div(ranch.totalStakedAmount || 0) : new BigNumber(0);
  

  const [showExpandableSection, setShowExpandableSection] = useState(false)
  const { 
    address, feeAddress, owner, token, lpToken, 
    emissionPerBlock, depositFeeRate, startBlock, 
    isStarted, isEnded, balance, lpBalance, userInfo 
  } = ranch;
  const isOwner = account && owner && owner.toLowerCase() === account.toLowerCase();
  const isPrivileged = isAddressMatch(getEbenAddress(), token.address) 
    || isAddressMatch(getEbenAddress(), lpToken.token0.address) 
    || isAddressMatch(getEbenAddress(), lpToken.token1.address);

  const status = isEnded && TranslateString(1071,"Ended") || (isStarted ? TranslateString(1070,"Active") : TranslateString(1072,"Pending"));
  const emissionPerYear = emissionPerBlock.times(BLOCKS_PER_YEAR);
  const lpPrice = lpToken.totalSupply && lpToken.totalSupply.isGreaterThan(0) ? (lpToken.value || new BigNumber(0)).div(lpToken.totalSupply) : new BigNumber(0);
  let lpStakedValue = lpPrice.times(lpBalance);
  if (lpToken.value && lpToken.value.isGreaterThan(0) && !lpStakedValue.isGreaterThan(0)) lpStakedValue = new BigNumber(1);
  const apy = (isEnded|| !lpStakedValue.isGreaterThan(0)) ? new BigNumber(0) : emissionPerYear.times(token.price||0).div(lpStakedValue);
  const apyLabel = apy && apy.times(new BigNumber(100)).toNumber().toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const sortedTokensInLp = sortLpTokens(lpToken.token0, lpToken.token1);
  const lpLabel = `${sortedTokensInLp[0].symbol}-${sortedTokensInLp[1].symbol} LP`;
  
  const [favorited, setFavorited] = useState(isFavorite(address));
  const onFavoriteChange = () => {
    const newValue = !favorited;
    if (newValue) addFavorite(address);
    else removeFavorite(address);
    setFavorited(newValue);
    if (onFavoriteChanged) onFavoriteChanged(address, newValue);
  }

  const getStartsIn = () => {
    const n = Number(startBlock || 0);
    const remainingTime = (n- currentBlock)*5.5;
    const d = Math.floor(remainingTime/86400);
    const h = Math.floor((remainingTime%86400)/3600);
    const m = Math.floor((remainingTime%3600)/60);
    return `${d>0?`${d}d `:""}${h>0?`${h}h `:""}${m>0?`${m}m`:""}` || "0m";
  };

  return (
    <FCard>
      <FavoriteContainer>
        <StyledFavoriteButton onClick={onFavoriteChange}>
          <img src={`/images/ranches/${favorited?"favorited":"unfavorited"}.png`} alt="Favorite" width="32" height="32" title={favorited?TranslateString(1221,'Remove from Favorites'):TranslateString(1220,'Add to Favorites')} />
        </StyledFavoriteButton>
      </FavoriteContainer>
      {isPrivileged && <StyledCardAccent /> || ""}
      <CardHeading ranch={ranch} />
      <Flex justifyContent='space-between'>
        <Text>{TranslateString(437,'Status')}:</Text>
        <Text bold>
          {status}
        </Text>
      </Flex>
      {
        !isStarted && !isEnded && (
          <Flex justifyContent='space-between' style={{marginTop:"4px"}}>
            <Text>{TranslateString(1331,'Start Block')}:</Text>
            <Text bold>
              {startBlock}
            </Text>
          </Flex>
        )
      }
      {
        !isStarted && !isEnded && currentBlock>0 && (
          <Flex justifyContent='space-between' style={{marginTop:"4px"}}>
            <Text>{TranslateString(1332,'Starts In')}:</Text>
            <Text bold>
              {getStartsIn()}
            </Text>
          </Flex>
        )
      }
      <Flex justifyContent='space-between' alignItems='center'>
          <Text>{TranslateString(89,'APR')}:</Text>
          <Text bold style={{ display: 'flex', alignItems: 'center' }}>
            {apy.isGreaterThan(0) ? (
              <>
                <ApyButton
                  userDataState={!!ranch.userInfo}
                  tokenBalance={ranch.userInfo?ranch.userInfo.lpBalance:new BigNumber(0)}
                  stakedBalance={ranch.userInfo?ranch.userInfo.stakedLp:new BigNumber(0)}
                  multiplier={ranch.multiplier}
                  pledgeMode={2}
                  lpLabel={lpLabel}
                  quoteTokenAdresses={{10000: sortedTokensInLp[1].address}}
                  quoteTokenSymbol={sortedTokensInLp[1].symbol}
                  tokenAddresses={{10000: sortedTokensInLp[0].address}}
                  tokenPrice={token.price}
                  apy={apy}
                  harvestTokenSymbol={token.symbol}
                  bchPrice={bchPrice}
                  lpPrice={lpPrice}
                />
                {apyLabel}%
              </>
            ) : (
              <span style={{lineHeight: "32px"}}>N/A</span>
            )}
          </Text>
        </Flex>
      <Flex justifyContent='space-between'>
        <Text>{TranslateString(90, 'Earn')}:</Text>
        <Text bold>
          <span>{token.symbol}</span>
          <span style={{fontSize:'0.7em', marginLeft: '3px'}}>+ FEES</span>
        </Text>
      </Flex>
      {
        !isEnded && (
          <Flex justifyContent='space-between' style={{marginTop:"4px"}}>
            <Text>{TranslateString(1557, 'Remaining')}:</Text>
            <Text bold>
              <StyledText2>{getDisplayBalance(balance.toNumber())}</StyledText2>
              <StyledLinkExternal2 href={`https://smartscout.cash/address/${token.address}`}>
                {token.symbol}
              </StyledLinkExternal2>
            </Text>
          </Flex>
        )
      }
      {
        !isEnded && (
          <Flex justifyContent='space-between' style={{marginTop:"6px"}}>
            <Text style={{ fontSize: '24px' }}>{TranslateString(439,'Deposit Fee')}:</Text>
            <Text bold style={{ fontSize: '24px' }}>{depositFeeRate}%</Text>
          </Flex>
        )
      }
      <CardActionsContainer ranch={ranch} account={account} lpLabel={lpLabel}/>
      {
        account && !isEnded && (<ManagementLabel><span>{TranslateString(520,'Management')}</span></ManagementLabel>)
      }
      {
        account && !isEnded && (<CardActions2Container ranch={ranch} account={account} />)
      }
      <Divider />
      <ExpandableSectionButton
        onClick={() => setShowExpandableSection(!showExpandableSection)}
        expanded={showExpandableSection}
      />
      <ExpandingWrapper expanded={showExpandableSection}>
        <DetailsSection ranch={ranch} />
      </ExpandingWrapper>
    </FCard>
  )
}
export default Card
